import {
  getFloatingEdgeParams,
  sideFromData,
} from "@/pages/canvas/utils/floatingEdge";
import {
  buildLinePath,
  type LinePoint,
  type LineRouteFrame,
} from "@/pages/canvas/utils/linePath";
import {
  useReactFlow,
  useViewport,
  ViewportPortal,
  type Edge,
} from "@xyflow/react";
import { CANVAS_FLOATING_TOOLBAR_Z_INDEX } from "../constants/canvasFloatingToolbar.constants";
import { useCanvasUi } from "../context/CanvasUiContext";
import { useOpenEdgeContextMenu } from "../hooks/useOpenEdgeContextMenu";
import { readEdgeStyle } from "../utils/applyEdgeStyle";
import {
  LineFloatingToolbar,
  type LineStyleFields,
} from "./LineFloatingToolbar";

interface EdgeFloatingToolbarProps {
  edge: Edge;
  onStyleChange: (edgeId: string, style: LineStyleFields) => void;
  onPopoverOpenChange?: (open: boolean) => void;
}

/**
 * Positions the line bar above a selected edge: centred on its label point,
 * clear of the highest point the line (or its label) reaches, so the bar
 * never covers the line or the grips it is there to edit. Both come from
 * the same path builder the edge renders with, so the bar never drifts.
 * Uses ViewportPortal so it stays in flow space and scales with zoom.
 */
export function EdgeFloatingToolbar({
  edge,
  onStyleChange,
  onPopoverOpenChange,
}: EdgeFloatingToolbarProps) {
  const openEdgeContextMenu = useOpenEdgeContextMenu();
  const { getInternalNode } = useReactFlow();
  const viewport = useViewport();
  const { editingEdgeId, setEditingEdgeId } = useCanvasUi();

  // The label bar owns the space while the label is being edited.
  if (editingEdgeId === edge.id) return null;

  const sourceNode = getInternalNode(edge.source);
  const targetNode = getInternalNode(edge.target);
  if (!sourceNode || !targetNode) return null;

  const data = (edge.data ?? {}) as {
    curve_type?: LineStyleFields["curve_type"];
    source_side?: unknown;
    target_side?: unknown;
    bend?: { x: number; y: number } | null;
    points?: LinePoint[] | null;
    points_frame?: LineRouteFrame | null;
  };
  const edgeStyle = readEdgeStyle(edge);
  const floating = getFloatingEdgeParams(sourceNode, targetNode, {
    sourceSide: sideFromData(data.source_side),
    targetSide: sideFromData(data.target_side),
  });
  if (!floating) return null;

  const built = buildLinePath({
    curve: edgeStyle.curve_type ?? "step",
    source: { x: floating.sx, y: floating.sy, side: floating.sourcePos },
    target: { x: floating.tx, y: floating.ty, side: floating.targetPos },
    bend: data.bend ?? null,
    points: data.points ?? null,
    pointsFrame: data.points_frame ?? null,
  });

  const hasLabel = typeof edge.label === "string" && edge.label.length > 0;
  // The bar is ~56px tall and grows downward from its anchor; the offset
  // clears it, plus a label sitting on the line's top run.
  const TOOLBAR_OFFSET = 76;
  const clearY = Math.min(built.top, built.label.y);

  return (
    <ViewportPortal>
      <div
        className="canvas-floating-toolbar-portal"
        style={{
          position: "absolute",
          left: built.label.x,
          top: clearY - TOOLBAR_OFFSET / viewport.zoom,
          transform: `translateX(-50%) scale(${1 / viewport.zoom})`,
          transformOrigin: "top center",
          pointerEvents: "auto",
          zIndex: CANVAS_FLOATING_TOOLBAR_Z_INDEX,
        }}
        onPointerDown={(e) => e.stopPropagation()}
      >
        <div className="h-2 w-full" />
        <LineFloatingToolbar
          variant="edge"
          style={edgeStyle}
          onStyleChange={(patch) => onStyleChange(edge.id, patch)}
          onPopoverOpenChange={onPopoverOpenChange}
          hasLabel={hasLabel}
          onEditLabel={() => setEditingEdgeId(edge.id)}
          onOpenMenu={(anchor) => openEdgeContextMenu.anchored(edge.id, anchor)}
        />
        <div className="h-2 w-full" />
      </div>
    </ViewportPortal>
  );
}
